/**
 * BankMatchingCard.jsx — tek bir eşleşmemiş banka hareketi kartı
 * (bkz. AdminBankMatchingPool.jsx). Desen kaynağı: MokaQueueCard.jsx.
 *
 * Öneri alanı (`suggested_payment_id`/`suggested_payment_ref`/`match_confidence`/
 * `match_method`) YALNIZCA "Otomatik Eşleştir" daha önce çalıştırılmışsa doludur
 * (bkz. bankMatching.js modül notu).
 *
 * Tur 2 (2026-09-29) — `suggested_student_name`/`suggested_course_title`/
 * `suggested_amount`/`suggested_due_date` alanları VARSA gösterilir, YOKSA
 * eski davranışa (yalnızca payment_ref + gönderen adı karşılaştırması)
 * sessizce düşülür. Tur 3'te backend (services/bank_transaction_service.py::
 * list_unmatched, salt okundu) HAZIR olarak doğrulandı — bu beş alan artık
 * HER ZAMAN dolu gelir (`suggested_payment_id`/`suggested_installment_id`
 * doluysa).
 *
 * Sözleşme güncellemesi (aynı gün, koordinatör mesajı — madde C): açık
 * taksitlerin pending `payments` kaydı YOK, backend taksite DOĞRUDAN
 * eşleştirme ekliyor. `match_type` ("payment"|"installment") hem öneri
 * alanında (`suggested_payment_id`/`suggested_installment_id`) hem "Elle
 * Seç" listesinde (`useStudentPendingPayments`) belirleyicidir — hangi
 * alanın (`payment_id` veya `installment_id`) `match` çağrısına gideceğini
 * seçer (bkz. `useMatchBankTransaction`, ikisi BİRDEN gönderilmez).
 *
 * Para kararı olduğu için hem "Önerileni Eşleştir" hem "Elle Seç" TEK TIKLA
 * yürümez — ikisi de bir onay diyaloğuna düşer (Moka kalıbıyla aynı disiplin).
 *
 * `ManualMatchPanel` bilerek AYRI bir bileşen: `useStudentList` yalnızca panel
 * açıkken (manualOpen=true) MOUNT edilsin diye — aksi halde sayfadaki her kart
 * (ör. 20 satır) mount anında paralel "GET /admin/users?search=" atardı (hook
 * `enabled` desteklemiyor, bkz. src/api/queries/students.js). Öğrenci
 * seçildiğinde `useStudentPendingPayments` (Tur 3'te backend HAZIR olarak
 * doğrulandı) o öğrencinin açık kayıtlarını listeler — elle `payment_id`
 * metin girişi KALDIRILDI (koordinatör talimatı: "artık liste var").
 *
 * Tur 4 (elle test bulguları, HIZ MODU) — üç değişiklik:
 *   1) BUG FIX: `useConfirmDialog`'un ortak `ConfirmDialog` bileşeni Radix
 *      `AlertDialogAction`'ı kullanır — bu, `onConfirm` İÇİNDE hata olsa
 *      BİLE tıklamada dialog'u OTOMATİK kapatır (preventDefault çağrılmaz,
 *      bkz. `ReversePaymentDialog.jsx` dosya başı notundaki AYNI tuzak).
 *      Elle test bulgusu: 400 `tutar_uyusmuyor`/`taksit_uygun_degil` gibi
 *      hatalarda panel kapanıp kart eski haline dönüyordu, hata YALNIZCA
 *      toast'ta görünüyordu (kaybolan bir uyarı). Çözüm: paylaşılan
 *      `ConfirmDialog` YERİNE bu dosyaya özel `MatchConfirmDialog` (ham
 *      `AlertDialog*` primitifleri, `ReversePaymentDialog.jsx` İLE AYNI
 *      desen) — hata durumunda dialog AÇIK kalır, hata mesajı hem toast'ta
 *      HEM diyalog içinde inline gösterilir.
 *   2) `item.name_match` (⚠️ backend bu turda YAZIYOR, kontrata göre
 *      bağlandı) — gönderenin adı bir öğrenciyle SERBEST metin eşleşmesiyle
 *      bulunursa (öneri motorundan BAĞIMSIZ, `suggested_*` alanlarından
 *      farklı bir kaynak) o öğrencinin açık kalemlerini gösterir; tutarı
 *      tutan bir kalem varsa satırında doğrudan eşleştirme butonu. `name_
 *      match.user_id` ile öneri motorunun `suggested_user_id`'si FARKLIYSA
 *      (eski/uyumsuz veri, iki farklı öğrenciye işaret ediyor demektir)
 *      öneri bloğunda uyarı rozeti + "Önerileni Eşleştir" onay diyaloğunda
 *      EK bir onay kutusu (işaretlenmeden gönderilemez).
 *   3) Tarihler (`value_date`, `suggested_due_date`, name_match açık kalem
 *      `due_date`) artık `fmtDateOnly` ile gg.aa.yyyy; banka adı boşsa
 *      `banks` prop'undan (AdminBankMatchingPool.jsx'in zaten çektiği
 *      `useAccountingBanks`) `bank_id` ile çözülür.
 */
import { useState, useEffect } from 'react';
import {
  CheckCircle2, XCircle, User, Calendar, Hash, Landmark, Search, ExternalLink,
  ArrowDownCircle, ArrowUpCircle, GraduationCap, AlertTriangle,
} from 'lucide-react';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/contexts/ToastContext';
import { getApiErrorMessage } from '@/utils/apiError';
import { fmtTRY } from '@/utils/formatCurrency';
import { fmtDateOnly } from '@/utils/formatDateOnly';
import { StatusBadge } from '@/components/admin';
import { useMatchBankTransaction, useIgnoreBankTransaction } from '@/api/mutations/bankMatching';
import { useStudentList, useStudentPendingPayments } from '@/api/queries/students';

const AMOUNT_EPS = 0.01;

function confidenceVariant(score) {
  if (score >= 100) return 'success';
  if (score >= 90) return 'success';
  if (score >= 70) return 'warning';
  return 'neutral';
}

/** Bekleyen/açık kayıt -> `useMatchBankTransaction` body şekli (`match_type` belirleyici). */
function pendingItemToMatchArgs(item) {
  if (!item) return null;
  if (item.match_type === 'installment') return { installmentId: item.installment_id };
  return { paymentId: item.payment_id };
}

function amountsMatch(a, b) {
  if (typeof a !== 'number' || typeof b !== 'number') return false;
  return Math.abs(a - b) < AMOUNT_EPS;
}

/**
 * Tur 4 — ortak onay diyaloğu (Radix `AlertDialogAction` KULLANILMAZ, bkz.
 * dosya başı notu madde 1). Hata durumunda `onOpenChange(false)` ÇAĞRILMAZ —
 * diyalog açık kalır, `errorMessage` inline gösterilir. `requireAck` true
 * ise onay kutusu işaretlenmeden `onConfirm` DEVREDIŞI (buton disabled
 * DEĞİL — tıklanınca `ackRequiredMessage` inline gösterilir, projedeki
 * "pasif buton yerine tıklanınca hata" konvansiyonuyla tutarlı).
 */
function MatchConfirmDialog({
  open, onOpenChange, title, description, confirmLabel, cancelLabel,
  onConfirm, loading, errorMessage, variant = 'warning',
  requireAck, ackLabel, ackRequiredMessage,
}) {
  const [ackChecked, setAckChecked] = useState(false);
  const [ackSubmitted, setAckSubmitted] = useState(false);

  useEffect(() => {
    if (open) { setAckChecked(false); setAckSubmitted(false); }
  }, [open]);

  // Renk şeması mevcut paylaşılan `ConfirmDialog` (components/admin/ConfirmDialog.jsx)
  // VARIANTS'ı İLE AYNI — yalnızca dialog kabuğu değişti, görsel dil DEĞİŞMEDİ.
  const iconColor = variant === 'danger' ? 'text-[var(--brand-danger)]' : 'text-amber-500';
  const confirmClass = variant === 'danger'
    ? 'bg-[var(--brand-danger)] hover:bg-[var(--brand-danger)]/90 text-white border-[var(--brand-danger-shadow)] border-b-4'
    : 'bg-amber-500 hover:bg-amber-500/90 text-white border-amber-700 border-b-4';

  const handleConfirm = () => {
    if (requireAck && !ackChecked) {
      setAckSubmitted(true);
      return;
    }
    onConfirm();
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o && !loading) onOpenChange(false); }}>
      <AlertDialogContent className="rounded-2xl border-2 border-[var(--border-light)] max-w-md bg-white space-y-3">
        <AlertDialogHeader>
          <div className="flex items-start gap-2.5">
            <AlertTriangle size={20} className={`mt-0.5 shrink-0 ${iconColor}`} />
            <div>
              <AlertDialogTitle className="font-display text-base font-bold text-[var(--text-main)]">
                {title}
              </AlertDialogTitle>
              {description && (
                <AlertDialogDescription className="mt-1 text-xs text-[var(--text-muted)]">
                  {description}
                </AlertDialogDescription>
              )}
            </div>
          </div>
        </AlertDialogHeader>

        {errorMessage && (
          <p role="alert" className="text-xs font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
            {errorMessage}
          </p>
        )}

        {requireAck && (
          <div className="space-y-1">
            <label className="flex items-start gap-2 text-xs">
              <input
                type="checkbox"
                checked={ackChecked}
                onChange={(e) => { setAckChecked(e.target.checked); setAckSubmitted(false); }}
                className="mt-0.5"
                data-testid="bank-match-conflict-ack-checkbox"
              />
              <span className="font-semibold">{ackLabel}</span>
            </label>
            {ackSubmitted && !ackChecked && (
              <p role="alert" className="text-[11px] font-bold text-rose-600">{ackRequiredMessage}</p>
            )}
          </div>
        )}

        <AlertDialogFooter className="gap-2 mt-1">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="px-4 py-2 rounded-xl border-2 border-[var(--border-heavy)] font-bold text-xs h-9 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading}
            data-testid="bank-match-dialog-confirm-btn"
            className={`px-4 py-2 rounded-xl font-bold text-xs h-9 disabled:opacity-50 inline-flex items-center gap-1.5 ${confirmClass}`}
          >
            <CheckCircle2 size={14} />
            {confirmLabel}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ManualMatchPanel({ transactionId, onCancel, onSubmit, submitting, t, lang }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedPendingItem, setSelectedPendingItem] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: studentData, isFetching: studentsLoading } = useStudentList({
    search: debouncedQuery.length >= 2 ? debouncedQuery : '',
    limit: 5,
  });
  const studentResults = debouncedQuery.length >= 2 ? (studentData?.users || []) : [];

  // Tur 2 — öğrenci seçilince açık kayıtları (ödeme VEYA taksit, `match_type`
  // ile ayrışır) listele (backend Tur 3'te HAZIR doğrulandı — bkz.
  // useStudentPendingPayments docstring'i).
  const { data: pendingData, isFetching: pendingLoading, isError: pendingError } = useStudentPendingPayments(
    selectedStudent?.user_id
  );
  const pendingPayments = pendingData?.items || [];
  const pendingItemKey = (p) => p.payment_id || p.installment_id;

  const handleSubmit = () => {
    setSubmitted(true);
    if (!selectedPendingItem) return;
    onSubmit(pendingItemToMatchArgs(selectedPendingItem));
  };

  return (
    <div className="space-y-2">
      <div>
        <label htmlFor={`bank-match-student-search-${transactionId}`} className="text-xs font-bold block mb-0.5">
          {t('admin.bank_match_student_search_label')}
        </label>
        <input
          id={`bank-match-student-search-${transactionId}`}
          type="text"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setSelectedStudent(null); }}
          placeholder={t('admin.bank_match_student_search_placeholder')}
          data-testid="bank-match-student-search-input"
          className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 text-xs"
        />
        {debouncedQuery.length >= 2 && (
          <div className="mt-1.5 flex flex-col gap-1">
            {studentsLoading ? (
              <p className="text-[11px] text-[var(--text-muted)]">{t('admin.bank_match_searching')}</p>
            ) : studentResults.length === 0 ? (
              <p className="text-[11px] text-[var(--text-muted)]">{t('admin.bank_match_no_student_found')}</p>
            ) : (
              studentResults.map((s) => (
                <button
                  key={s.user_id}
                  type="button"
                  onClick={() => { setSelectedStudent(s); setSelectedPendingItem(null); setSubmitted(false); }}
                  data-testid={`bank-match-student-option-${s.user_id}`}
                  className={`text-left px-3 py-1.5 rounded-lg border-2 text-xs flex items-center justify-between gap-2 ${
                    selectedStudent?.user_id === s.user_id
                      ? 'border-[var(--brand-primary)] bg-sky-50'
                      : 'border-[var(--border-light)] hover:border-[var(--brand-primary)]/50'
                  }`}
                >
                  <span><strong>{s.name || '—'}</strong> — {s.email || '—'}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {selectedStudent && (
        <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-200 text-xs">
          <span><strong>{selectedStudent.name}</strong> — {selectedStudent.email}</span>
          <a
            href={`/admin/students/${selectedStudent.user_id}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="bank-match-student-detail-link"
            className="inline-flex items-center gap-1 font-bold text-[var(--brand-primary)] hover:underline shrink-0"
          >
            {t('admin.bank_match_open_student_detail')} <ExternalLink size={12} />
          </a>
        </div>
      )}

      {selectedStudent && pendingLoading && (
        <p className="text-[11px] text-[var(--text-muted)]">{t('admin.bank_match_pending_payments_loading')}</p>
      )}

      {selectedStudent && !pendingLoading && pendingError && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-2.5 py-1.5">
          {t('admin.bank_match_pending_payments_error')}
        </p>
      )}

      {selectedStudent && !pendingLoading && !pendingError && pendingPayments.length === 0 && (
        <p className="text-[11px] text-[var(--text-muted)]">{t('admin.bank_match_pending_payments_empty')}</p>
      )}

      {selectedStudent && !pendingLoading && !pendingError && pendingPayments.length > 0 && (
        <div>
          <p className="text-xs font-bold mb-1">{t('admin.bank_match_pending_payments_label')}</p>
          <div className="flex flex-col gap-1">
            {pendingPayments.map((p) => (
              <button
                key={pendingItemKey(p)}
                type="button"
                onClick={() => { setSelectedPendingItem(p); setSubmitted(false); }}
                data-testid={`bank-match-pending-payment-option-${pendingItemKey(p)}`}
                className={`text-left px-3 py-1.5 rounded-lg border-2 text-xs flex items-center justify-between gap-2 ${
                  selectedPendingItem && pendingItemKey(selectedPendingItem) === pendingItemKey(p)
                    ? 'border-[var(--brand-primary)] bg-sky-50'
                    : 'border-[var(--border-light)] hover:border-[var(--brand-primary)]/50'
                }`}
              >
                <span>
                  <strong>{p.course_title || p.payment_ref || pendingItemKey(p)}</strong>
                  {p.installment_no ? ` — #${p.installment_no}` : ''}
                  {p.due_date ? ` — ${fmtDateOnly(p.due_date, lang)}` : ''}
                </span>
                <span className="font-bold tabular-nums shrink-0">{fmtTRY(p.amount)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {submitted && !selectedPendingItem && (
        <p role="alert" className="text-[11px] font-bold text-rose-600 mt-0.5">
          {t('admin.bank_match_pending_payment_required')}
        </p>
      )}

      <div className="flex items-center gap-2">
        <button
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="bank-match-manual-confirm-open-btn"
          className="btn-success px-4 py-2 text-xs font-bold disabled:opacity-50 min-h-[40px] inline-flex items-center gap-1.5"
        >
          <CheckCircle2 size={14} />
          {t('admin.bank_match_manual_match_button')}
        </button>
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-xs text-[var(--text-muted)] min-h-[36px]"
        >
          {t('admin.bank_match_cancel_selection')}
        </button>
      </div>
    </div>
  );
}

export default function BankMatchingCard({ item, t, lang, banks = [] }) {
  const toast = useToast();
  const [manualOpen, setManualOpen] = useState(false);
  const [pendingManualArgs, setPendingManualArgs] = useState(null);
  const [pendingNameMatchArgs, setPendingNameMatchArgs] = useState(null);
  const [suggestConfirmOpen, setSuggestConfirmOpen] = useState(false);
  const [manualConfirmOpen, setManualConfirmOpen] = useState(false);
  const [nameMatchConfirmOpen, setNameMatchConfirmOpen] = useState(false);
  const [ignoreConfirmOpen, setIgnoreConfirmOpen] = useState(false);
  const [matchErrorMessage, setMatchErrorMessage] = useState('');

  const match = useMatchBankTransaction();
  const ignore = useIgnoreBankTransaction();

  // Sözleşme güncellemesi — öneri `match_type`'a göre payment_id VEYA
  // installment_id taşır (ikisi birden değil, bkz. dosya başı notu).
  const suggestedIsInstallment = item.match_type === 'installment' || (!item.suggested_payment_id && !!item.suggested_installment_id);
  const suggestedMatchArgs = suggestedIsInstallment
    ? { installmentId: item.suggested_installment_id }
    : { paymentId: item.suggested_payment_id };
  const hasSuggestion = suggestedIsInstallment ? !!item.suggested_installment_id : !!item.suggested_payment_id;
  const hasRecognizedRefWithoutPayment = !hasSuggestion && !!item.suggested_payment_ref;
  const directionIn = item.direction !== 'out';

  // Tur 4 — banka adı boşsa `banks` prop'undan (bank_id ile) çöz.
  const resolvedBankName = item.bank_name || banks.find((b) => b.bank_id === item.bank_id)?.name || '—';

  // Tur 4 — gönderen adı serbest metin eşleşmesi (⚠️ backend bu turda
  // YAZIYOR, kontrata göre bağlandı — bkz. dosya başı notu madde 2).
  const nameMatch = item.name_match || null;
  const nameMatchOpenItems = nameMatch?.open_items || [];
  // Öneri motoruyla (suggested_user_id) ÇAKIŞMA: iki farklı kaynak iki farklı
  // öğrenciye işaret ediyor — eski/uyumsuz veri riski.
  const hasNameConflict = !!(
    nameMatch && hasSuggestion && item.suggested_user_id && nameMatch.user_id
    && nameMatch.user_id !== item.suggested_user_id
  );

  const doMatch = async (matchArgs, onSuccess) => {
    setMatchErrorMessage('');
    try {
      const res = await match.mutateAsync({ transactionId: item.transaction_id, ...matchArgs });
      if (res?.amount_mismatch) {
        toast.warning(t('admin.bank_match_amount_mismatch_warning'));
      } else {
        toast.success(t('admin.bank_match_success'));
      }
      onSuccess();
    } catch (err) {
      // Tur 4 fix — diyalog AÇIK kalır (çağıran taraf dialog'u kapatmaz),
      // hata hem toast'ta hem burada (inline) gösterilir.
      const msg = getApiErrorMessage(err, lang);
      setMatchErrorMessage(msg);
      toast.error(msg);
    }
  };

  const doIgnore = async () => {
    try {
      await ignore.mutateAsync(item.transaction_id);
      toast.success(t('admin.bank_match_ignore_success'));
      setIgnoreConfirmOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
      setIgnoreConfirmOpen(false);
    }
  };

  const handleManualSubmit = (matchArgs) => {
    setPendingManualArgs(matchArgs);
    setMatchErrorMessage('');
    setManualConfirmOpen(true);
  };

  const openNameMatchConfirm = (openItem) => {
    setPendingNameMatchArgs(pendingItemToMatchArgs(openItem));
    setMatchErrorMessage('');
    setNameMatchConfirmOpen(true);
  };

  return (
    <div className="card-clay p-4" data-testid={`bank-match-card-${item.transaction_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-3 flex-wrap text-xs text-[var(--text-muted)]">
          <span className="font-bold text-base text-[var(--text-main)]">{fmtTRY(item.amount)}</span>
          {directionIn ? (
            <span className="flex items-center gap-1 text-emerald-600 font-semibold">
              <ArrowDownCircle size={13} /> {t('admin.bank_match_direction_in')}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-rose-600 font-semibold">
              <ArrowUpCircle size={13} /> {t('admin.bank_match_direction_out')}
            </span>
          )}
          <span className="flex items-center gap-1"><User size={12} /> {item.counterparty || '—'}</span>
          <span className="flex items-center gap-1"><Calendar size={12} /> {fmtDateOnly(item.value_date, lang) || '—'}</span>
          <span className="flex items-center gap-1"><Landmark size={12} /> {resolvedBankName}</span>
          <span className="flex items-center gap-1 font-mono"><Hash size={11} /> {item.transaction_id}</span>
        </div>
      </div>

      {item.description && (
        <p className="text-xs text-[var(--text-muted)] mb-3 bg-gray-50 rounded-lg px-2.5 py-1.5">
          {item.description}
        </p>
      )}

      {/* ── Sistem önerisi ──────────────────────────────────────────────────── */}
      <div className="mb-3">
        <p className="text-xs font-bold mb-1.5">{t('admin.bank_match_suggestion_title')}</p>
        {hasSuggestion ? (
          <div className="flex flex-col gap-2 px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-sky-50 text-xs">
            {item.suggested_student_name && (
              <div className="flex flex-wrap items-center gap-2 font-semibold text-[var(--text-main)]">
                <GraduationCap size={13} className="text-[var(--brand-primary)]" />
                {item.suggested_student_name}
                {item.suggested_course_title && <span className="text-[var(--text-muted)] font-normal">— {item.suggested_course_title}</span>}
                {hasNameConflict && (
                  <StatusBadge variant="danger" label={t('admin.bank_match_name_conflict_badge')} />
                )}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              {item.suggested_payment_ref && <span className="font-mono font-semibold">{item.suggested_payment_ref}</span>}
              {typeof item.suggested_amount === 'number' && (
                <span className="font-semibold">{fmtTRY(item.suggested_amount)}</span>
              )}
              {item.suggested_due_date && (
                <span className="text-[var(--text-muted)] flex items-center gap-1"><Calendar size={11} /> {fmtDateOnly(item.suggested_due_date, lang)}</span>
              )}
              <StatusBadge
                variant={confidenceVariant(item.match_confidence)}
                label={`${item.match_confidence ?? '—'}%`}
              />
              {item.match_method && (
                <span className="text-[var(--text-muted)]">
                  {t(`admin.bank_match_method_${item.match_method}`) || item.match_method}
                </span>
              )}
              <button
                onClick={() => { setMatchErrorMessage(''); setSuggestConfirmOpen(true); }}
                disabled={match.isPending}
                data-testid="bank-match-suggest-btn"
                className="btn-success ml-auto px-3 py-1.5 text-xs font-bold disabled:opacity-50 min-h-[36px] inline-flex items-center gap-1.5"
              >
                <CheckCircle2 size={14} />
                {t('admin.bank_match_suggest_button')}
              </button>
            </div>
          </div>
        ) : hasRecognizedRefWithoutPayment ? (
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-2.5 py-1.5">
            {t('admin.bank_match_ref_recognized_no_payment')} <span className="font-mono font-semibold">{item.suggested_payment_ref}</span>
          </p>
        ) : (
          <p className="text-[11px] text-[var(--text-muted)]">{t('admin.bank_match_no_suggestion')}</p>
        )}
      </div>

      {/* ── Gönderen adı eşleşmesi (Tur 4) ───────────────────────────────────── */}
      {nameMatch && (
        <div className="mb-3 px-3 py-2 rounded-xl border-2 border-violet-200 bg-violet-50/60 text-xs space-y-1.5" data-testid="bank-match-name-match-box">
          <p className="font-semibold flex items-center gap-1.5 text-[var(--text-main)]">
            <GraduationCap size={13} className="text-violet-600" />
            {t('admin.bank_match_name_match_info').replace('{student}', nameMatch.student_name || '—')}
          </p>
          {nameMatchOpenItems.length === 0 ? (
            <p className="text-[var(--text-muted)]">{t('admin.bank_match_name_match_no_amount_match')}</p>
          ) : (
            <div className="flex flex-col gap-1">
              {nameMatchOpenItems.map((oi) => {
                const key = oi.payment_id || oi.installment_id;
                const isAmountMatch = amountsMatch(oi.amount, item.amount);
                return (
                  <div
                    key={key}
                    className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg border-2 ${
                      isAmountMatch ? 'border-emerald-300 bg-emerald-50' : 'border-[var(--border-light)] bg-white'
                    }`}
                    data-testid={`bank-match-name-match-item-${key}`}
                  >
                    <span>
                      <strong>{oi.course_title || '—'}</strong>
                      {oi.installment_no ? ` — #${oi.installment_no}` : ''}
                      {oi.due_date ? ` — ${fmtDateOnly(oi.due_date, lang)}` : ''}
                      {' — '}
                      <span className="font-bold">{fmtTRY(oi.amount)}</span>
                    </span>
                    {isAmountMatch && (
                      <button
                        type="button"
                        onClick={() => openNameMatchConfirm(oi)}
                        disabled={match.isPending}
                        data-testid={`bank-match-name-match-btn-${key}`}
                        className="btn-success px-2.5 py-1 text-[11px] font-bold disabled:opacity-50 shrink-0 inline-flex items-center gap-1"
                      >
                        <CheckCircle2 size={12} />
                        {t('admin.bank_match_name_match_match_button')}
                      </button>
                    )}
                  </div>
                );
              })}
              {!nameMatchOpenItems.some((oi) => amountsMatch(oi.amount, item.amount)) && (
                <p className="text-[var(--text-muted)]">{t('admin.bank_match_name_match_no_amount_match')}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Elle seç ─────────────────────────────────────────────────────────── */}
      <div className="border-t border-[var(--border-light)] pt-3">
        {!manualOpen ? (
          <button
            onClick={() => setManualOpen(true)}
            data-testid="bank-match-manual-open-btn"
            className="text-xs font-bold text-[var(--brand-primary)] hover:underline inline-flex items-center gap-1"
          >
            <Search size={13} /> {t('admin.bank_match_manual_button')}
          </button>
        ) : (
          <ManualMatchPanel
            transactionId={item.transaction_id}
            onCancel={() => setManualOpen(false)}
            onSubmit={handleManualSubmit}
            submitting={match.isPending}
            t={t}
            lang={lang}
          />
        )}
      </div>

      {/* ── Yoksay ───────────────────────────────────────────────────────────── */}
      <div className="mt-3 pt-3 border-t border-[var(--border-light)]">
        <button
          onClick={() => setIgnoreConfirmOpen(true)}
          data-testid="bank-match-ignore-btn"
          className="text-xs font-bold text-rose-600 hover:underline inline-flex items-center gap-1"
        >
          <XCircle size={13} /> {t('admin.bank_match_ignore_button')}
        </button>
      </div>

      <MatchConfirmDialog
        open={suggestConfirmOpen}
        onOpenChange={(o) => { if (!o) { setSuggestConfirmOpen(false); setMatchErrorMessage(''); } }}
        title={t('admin.bank_match_suggest_confirm_title')}
        description={hasNameConflict ? t('admin.bank_match_name_conflict_warning') : t('admin.bank_match_suggest_confirm_desc')}
        cancelLabel={t('admin.btn_cancel')}
        confirmLabel={t('admin.bank_match_suggest_button')}
        variant={hasNameConflict ? 'danger' : 'warning'}
        onConfirm={() => doMatch(suggestedMatchArgs, () => { setSuggestConfirmOpen(false); setMatchErrorMessage(''); })}
        loading={match.isPending}
        errorMessage={matchErrorMessage}
        requireAck={hasNameConflict}
        ackLabel={t('admin.bank_match_name_conflict_ack')}
        ackRequiredMessage={t('admin.bank_match_name_conflict_ack_required')}
      />
      <MatchConfirmDialog
        open={manualConfirmOpen}
        onOpenChange={(o) => { if (!o) { setManualConfirmOpen(false); setMatchErrorMessage(''); } }}
        title={t('admin.bank_match_manual_confirm_title')}
        description={t('admin.bank_match_manual_confirm_desc')}
        cancelLabel={t('admin.btn_cancel')}
        confirmLabel={t('admin.bank_match_manual_match_button')}
        onConfirm={() => doMatch(pendingManualArgs, () => { setManualConfirmOpen(false); setManualOpen(false); setMatchErrorMessage(''); })}
        loading={match.isPending}
        errorMessage={matchErrorMessage}
      />
      <MatchConfirmDialog
        open={nameMatchConfirmOpen}
        onOpenChange={(o) => { if (!o) { setNameMatchConfirmOpen(false); setMatchErrorMessage(''); } }}
        title={t('admin.bank_match_manual_confirm_title')}
        description={t('admin.bank_match_manual_confirm_desc')}
        cancelLabel={t('admin.btn_cancel')}
        confirmLabel={t('admin.bank_match_name_match_match_button')}
        onConfirm={() => doMatch(pendingNameMatchArgs, () => { setNameMatchConfirmOpen(false); setMatchErrorMessage(''); })}
        loading={match.isPending}
        errorMessage={matchErrorMessage}
      />
      <MatchConfirmDialog
        open={ignoreConfirmOpen}
        onOpenChange={(o) => { if (!o) setIgnoreConfirmOpen(false); }}
        title={t('admin.bank_match_ignore_confirm_title')}
        description={t('admin.bank_match_ignore_confirm_desc')}
        cancelLabel={t('admin.btn_cancel')}
        confirmLabel={t('admin.bank_match_ignore_button')}
        variant="danger"
        onConfirm={doIgnore}
        loading={ignore.isPending}
      />
    </div>
  );
}
